import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider } from 'antd';
import 'antd/dist/reset.css';
import zhCN from 'antd/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import App from './App';
import { installShellBack } from '@/utils/shellBack';
import './index.css';
installShellBack();
// Webpack 5 + Electron 环境兼容：部分依赖会在运行期读取 global
// 这里在入口最早处显式注入，避免 "global is not defined"
globalThis.global = globalThis;
console.log('[renderer] boot', {
    href: window.location.href,
    hasAppEl: !!document.getElementById('app'),
    hasRootEl: !!document.getElementById('root'),
    readyState: document.readyState,
});
/** 通知 index.html 隐藏启动遮罩（必须与 DOMContentLoaded 解耦：bundle 常在事件之后执行） */
function dispatchAppLoaded(reason) {
    try {
        console.log('[renderer] app-loaded', reason);
        window.dispatchEvent(new Event('app-loaded'));
    }
    catch (e) {
        console.error('[renderer] dispatch app-loaded failed', e);
    }
}
function scheduleAppLoaded(reason, ms) {
    window.setTimeout(() => dispatchAppLoaded(reason), ms);
}
// 配置dayjs本地化
dayjs.locale('zh-cn');
// 应用主题配置
const theme = {
    token: {
        colorPrimary: '#1890ff',
        borderRadius: 6,
        colorBgContainer: '#ffffff',
        colorBorder: '#d9d9d9',
        colorText: '#333333',
        colorTextSecondary: '#666666',
        colorTextTertiary: '#999999',
        fontSize: 14,
        lineHeight: 1.5715,
    },
    components: {
        Layout: {
            headerBg: '#001529',
            headerColor: '#ffffff',
            siderBg: '#001529',
            triggerBg: '#002140',
            triggerColor: '#ffffff',
        },
        Menu: {
            darkItemBg: '#001529',
            darkItemColor: '#ffffff',
            darkItemSelectedBg: '#1890ff',
            darkItemSelectedColor: '#ffffff',
        },
        Table: {
            headerBg: '#fafafa',
            headerColor: '#333333',
            rowHoverBg: '#f5f5f5',
        },
        Button: {
            defaultBg: '#ffffff',
            defaultBorderColor: '#d9d9d9',
            defaultColor: '#333333',
        },
    },
};
// 创建 React root
const root = ReactDOM.createRoot(document.getElementById('app'));
// 1) 脚本执行时 DOM 往往已是 interactive/complete，仅监听 DOMContentLoaded 会永远收不到事件
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scheduleAppLoaded('DOMContentLoaded', 0), { once: true });
}
else {
    scheduleAppLoaded('document-already-ready', 0);
}
// 错误边界
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }
    componentDidCatch(error, errorInfo) {
        console.error('React应用错误:', error, errorInfo);
        // 发送错误报告
        if (window.electronAPI) {
            window.electronAPI.log('error', 'React应用错误', {
                error: error.message,
                stack: error.stack,
                componentStack: errorInfo.componentStack,
            });
        }
    }
    render() {
        if (this.state.hasError) {
            return (_jsxs("div", { style: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    padding: 20,
                    textAlign: 'center',
                    backgroundColor: '#f0f2f5',
                }, children: [_jsx("div", { style: { fontSize: 48, marginBottom: 20 }, children: "\uD83D\uDE22" }), _jsx("h1", { style: { marginBottom: 16, color: '#333' }, children: "\u5E94\u7528\u9047\u5230\u9519\u8BEF" }), _jsx("p", { style: { marginBottom: 24, color: '#666', maxWidth: 600 }, children: this.state.error?.message || '未知错误' }), _jsxs("div", { style: { display: 'flex', gap: 12 }, children: [_jsx("button", { onClick: () => window.location.reload(), style: {
                                    padding: '8px 16px',
                                    backgroundColor: '#1890ff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                }, children: "\u91CD\u65B0\u52A0\u8F7D" }), _jsx("button", { onClick: () => {
                                    if (window.electronAPI) {
                                        window.electronAPI.closeWindow();
                                    }
                                }, style: {
                                    padding: '8px 16px',
                                    backgroundColor: '#f5f5f5',
                                    color: '#333',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                }, children: "\u9000\u51FA\u5E94\u7528" })] })] }));
        }
        return this.props.children;
    }
}
// 挂载 React 根
root.render(_jsx(React.StrictMode, { children: _jsx(ErrorBoundary, { children: _jsx(ConfigProvider, { locale: zhCN, theme: theme, children: _jsx(App, {}) }) }) }));
// 2) React 已挂载后再通知一次（首帧绘制后隐藏，避免闪白）
scheduleAppLoaded('after-root-render', 120);
// 3) 硬兜底：极端情况下仍保证遮罩消失
scheduleAppLoaded('hard-fallback', 4000);
if (module?.hot) {
    module.hot.accept();
}
/** 浏览器 / PWA：注册 Service Worker（Electron 不注册） */
function registerPwaServiceWorker() {
    if (typeof window === 'undefined')
        return;
    if (window.electronAPI)
        return;
    if (!('serviceWorker' in navigator))
        return;
    window.addEventListener('load', () => {
        const url = new URL('/sw.js', window.location.origin).href;
        navigator.serviceWorker
            .register(url, { scope: '/' })
            .then((reg) => console.log('[pwa] service worker', reg.scope))
            .catch((e) => console.warn('[pwa] service worker register failed', e));
    });
}
registerPwaServiceWorker();
// 全局错误处理
window.addEventListener('unhandledrejection', (event) => {
    console.error('未处理的Promise拒绝:', event.reason);
    if (window.electronAPI) {
        window.electronAPI.log('error', '未处理的Promise拒绝', {
            reason: event.reason?.message || String(event.reason),
            stack: event.reason?.stack,
        });
    }
});
window.addEventListener('error', (event) => {
    console.error('全局错误:', event.error);
    if (window.electronAPI) {
        window.electronAPI.log('error', '全局JavaScript错误', {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
            error: event.error?.message,
            stack: event.error?.stack,
        });
    }
});
